'use client';

import type { HandleChangeAnswerType } from '../model';
import type { AnswerUserType } from '~/model/AnswerUserType';
import type { SubQuestionType } from '~/model/SubQuestionType';
import { Fragment, useLayoutEffect } from 'react';
import { useFocus } from '~/hooks/useFocus';

export const StarRatingTypeQuestion = ({
  answerUser,
  questionId,
  handleChangeAnswer,
  subQuestions,
  type,
  disabled = false,
  langValue,
}: StarRatingTypeQuestionPropsType) => {
  const [inputRef, setInputFocus] = useFocus();

  useLayoutEffect(() => {
    setInputFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef.current, disabled]);

  return (
    <div
      className="control-rating"
      data-cy="choices"
      data-testid="star-rating"
    >
      {subQuestions?.map((subQuestion: SubQuestionType, indexSQ: number) => {
        const subQuestionUri = subQuestion['@id'];
        const userValue: number
          = answerUser?.[questionId]?.answer_choices[subQuestionUri] || null;

        return (
          <div
            key={`${questionId}-${subQuestion.id}`}
            data-testid="subquestion"
            className="control-rating__sub-question"
          >
            <label lang={langValue} className="vtmn-text-input_label">
              {subQuestion.title}
            </label>
            <div className="control-rating__support">
              <div className="vtmn-rating vtmn-rating_size--medium">
                <div className="vtmn-rating--interactive" role="radiogroup">
                  {Array.from({ length: 5 }, (_, index) => index + 1).map(
                    (value: number, index: number) => {
                      const uniqueKey = `${subQuestion.id}-${value}`;
                      const id = `star-${uniqueKey}`;

                      const shouldHaveRef = indexSQ === 0 && index === 0;

                      return (
                        <Fragment key={`rating-${uniqueKey}`}>
                          <input
                            ref={shouldHaveRef ? inputRef : null}
                            type="radio"
                            id={id}
                            name={`rating-${subQuestion.id}`}
                            value={value}
                            onChange={e => handleChangeAnswer(
                              +e.target.value,
                              questionId,
                              type,
                              subQuestionUri,
                            )}
                            checked={userValue === value}
                            disabled={disabled}
                          />
                          <label
                            htmlFor={id}
                            title={`${value}/5`}
                            role="presentation"
                          />
                        </Fragment>
                      );
                    },
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export interface StarRatingTypeQuestionPropsType {
  answerUser: AnswerUserType;
  questionId: string;
  handleChangeAnswer: HandleChangeAnswerType;
  subQuestions: SubQuestionType[];
  type: string;
  langValue: string;
  disabled?: boolean;
}
